import React, { useEffect, useMemo, useState } from 'react';
import { Button, DataTable, EmptyState, Input, Modal, SparkModalPortal, sparkToast } from '@spark';
import SettingsSectionCard from './SettingsSectionCard.jsx';
import { data_from_server_post } from '@/shared/http/client';
import { DateTimeWithTimeTooltip, handleCopy } from '@/shared/utils/helpers';
import './settings.css';

const PAGE_SIZE = 5;

const DEFAULT_KEY_NAME = 'SDK Key';

/**
 * API Keys tab — SDK keys management (list / create / revoke) rebuilt with
 * Spark DataTable + Modal on the shared section-card layout. The no-keys
 * empty state follows Figma `9939:17542` ("Ready to integrate?").
 */
const ApiKeysSection = () => {
    const [apiKeys, setApiKeys] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [createOpen, setCreateOpen] = useState(false);
    const [newKeyName, setNewKeyName] = useState(DEFAULT_KEY_NAME);
    const [createdSecret, setCreatedSecret] = useState(null);
    const [deleteCandidate, setDeleteCandidate] = useState(null);

    const fetchApiKeys = () => {
        setIsLoading(true);
        data_from_server_post(
            "list_api_keys",
            {},
            (result) => {
                setApiKeys(result.keys || []);
                setIsLoading(false);
            },
            (error) => {
                sparkToast.error("Failed to fetch API keys: " + error);
                setIsLoading(false);
            }
        );
    };

    useEffect(() => {
        fetchApiKeys();
    }, []);

    const handleCreate = () => {
        const trimmed = (newKeyName || '').trim();
        if (!trimmed) {
            sparkToast.error("Please enter a name");
            return;
        }
        const exists = (apiKeys || []).some(
            (k) => !k.revoked && (k.name || '').trim() === trimmed
        );
        if (exists) {
            sparkToast.error("An API key with this name already exists");
            return;
        }
        data_from_server_post(
            "create_api_key",
            trimmed,
            (result) => {
                setCreateOpen(false);
                setCreatedSecret(result.api_key);
                fetchApiKeys();
            },
            (error) => sparkToast.error("Failed to create API key: " + error)
        );
    };

    const openCreateModal = () => {
        setNewKeyName(DEFAULT_KEY_NAME);
        setCreateOpen(true);
    };

    const handleDelete = () => {
        if (!deleteCandidate) return;
        data_from_server_post(
            "delete_api_key",
            deleteCandidate.id,
            () => {
                sparkToast.success("API key deleted");
                setDeleteCandidate(null);
                fetchApiKeys();
            },
            (error) => {
                sparkToast.error("Failed to delete API key: " + error);
                setDeleteCandidate(null);
            }
        );
    };

    const columns = useMemo(() => [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (v) => v || 'SDK Key',
        },
        {
            title: 'Last 4',
            dataIndex: 'last4',
            key: 'last4',
            width: 110,
            render: (v) => (v ? `•••• ${v}` : ''),
        },
        {
            title: 'Created At',
            dataIndex: 'created_datetime',
            key: 'created_datetime',
            render: (v) => (v ? DateTimeWithTimeTooltip({ value: v }) : ''),
        },
        {
            title: 'Last used',
            dataIndex: 'last_used_at',
            key: 'last_used_at',
            render: (v) => (v ? DateTimeWithTimeTooltip({ value: v }) : '—'),
        },
        {
            title: 'Status',
            dataIndex: 'revoked',
            key: 'revoked',
            width: 110,
            render: (revoked) => (revoked ? 'Revoked' : 'Active'),
        },
        {
            title: '',
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Button
                    size="tiny"
                    variant="ghost"
                    danger
                    disabled={Boolean(record.revoked)}
                    disableWhenReadOnly
                    onClick={() => setDeleteCandidate(record)}
                >
                    Delete
                </Button>
            ),
        },
    ], []);

    const pagedKeys = useMemo(
        () => apiKeys.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [apiKeys, page]
    );

    return (
        <SettingsSectionCard iconSlug="lock-password-icon" title="API Keys">
            <p className="settings-text fs-unmask">
                API keys are used by the Python SDK to authenticate requests.
            </p>

            <div className="settings-api-keys__table">
                <DataTable
                    rowKey="id"
                    columns={columns}
                    dataSource={pagedKeys}
                    loading={isLoading}
                    pagination={
                        apiKeys.length > PAGE_SIZE
                            ? {
                                current: page,
                                pageSize: PAGE_SIZE,
                                total: apiKeys.length,
                                onChange: setPage,
                            }
                            : null
                    }
                    emptyText="No API keys yet"
                    emptyState={
                        <EmptyState
                            size="large"
                            illustration="illustration-api-keys"
                            illustrationWidth={257}
                            illustrationHeight={199}
                            title="Ready to integrate?"
                            subtitle="Let’s get your environment set up. Generate a unique API key to authenticate your SDK requests."
                            showMainButton
                            showCtaIcon
                            ctaLabel="Add API Key"
                            ctaDisableWhenReadOnly
                            onCtaClick={openCreateModal}
                        />
                    }
                />
            </div>

            {createOpen ? (
                <SparkModalPortal
                    open
                    onClose={() => setCreateOpen(false)}
                    size="slot"
                >
                    <Modal
                        title="Create API key"
                        showSubtitle={false}
                        showOverlay={false}
                        showScrollBar={false}
                        fixedHeight={false}
                        primaryLabel="Create"
                        tertiaryLabel="Cancel"
                        primaryDisableWhenReadOnly
                        onPrimaryClick={handleCreate}
                        onTertiaryClick={() => setCreateOpen(false)}
                        onCloseClick={() => setCreateOpen(false)}
                    >
                        <Input
                            label="Name"
                            size="large"
                            value={newKeyName}
                            onChange={(e) => setNewKeyName(e.target.value)}
                            placeholder="SDK Key"
                        />
                    </Modal>
                </SparkModalPortal>
            ) : null}

            {createdSecret ? (
                <SparkModalPortal
                    open
                    onClose={() => setCreatedSecret(null)}
                    size="slot"
                >
                    <Modal
                        title="API key created"
                        subtitle="Copy this API key now. You won't be able to see it again."
                        showOverlay={false}
                        showScrollBar={false}
                        fixedHeight={false}
                        primaryLabel="Copy"
                        tertiaryLabel="Close"
                        onPrimaryClick={() => handleCopy(createdSecret)}
                        onTertiaryClick={() => setCreatedSecret(null)}
                        onCloseClick={() => setCreatedSecret(null)}
                    >
                        <p className="settings-text" style={{ wordBreak: 'break-all', userSelect: 'all' }}>
                            {createdSecret}
                        </p>
                    </Modal>
                </SparkModalPortal>
            ) : null}

            {deleteCandidate ? (
                <SparkModalPortal
                    open
                    onClose={() => setDeleteCandidate(null)}
                    size="text"
                >
                    <Modal
                        title="Delete this API key?"
                        subtitle="This will revoke the key immediately. Clients using it will lose access."
                        showScrollBar={false}
                        fixedHeight={false}
                        showOverlay={false}
                        danger
                        primaryLabel="Yes, Delete"
                        tertiaryLabel="Cancel"
                        primaryDisableWhenReadOnly
                        onPrimaryClick={handleDelete}
                        onTertiaryClick={() => setDeleteCandidate(null)}
                        onCloseClick={() => setDeleteCandidate(null)}
                    />
                </SparkModalPortal>
            ) : null}
        </SettingsSectionCard>
    );
};

export default ApiKeysSection;
